/**
 * View Tags Modal Component
 * Modal dialog to display generated SPINE tags with detailed information
 */

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Button,
  Divider,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  ContentCopy,
  Close as CloseIcon,
  Tag as TagIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { useState, useEffect } from 'react';
import { getTagDetails, SpineTagDetails } from '../../services/tagGeneration.service';
import MathtasticIcon from '../common/MathtasticIcon';

interface ViewTagsModalProps {
  open: boolean;
  onClose: () => void;
  tagIds?: string[];
  lessonName?: string;
}

export default function ViewTagsModal({
  open,
  onClose,
  tagIds,
  lessonName
}: ViewTagsModalProps) {
  const [copySuccess, setCopySuccess] = useState<string | null>(null);
  const [tagDetails, setTagDetails] = useState<SpineTagDetails[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch tag details when modal opens
  useEffect(() => {
    if (open && tagIds && tagIds.length > 0) {
      setIsLoading(true);
      setError(null);
      getTagDetails(tagIds)
        .then(details => {
          setTagDetails(details);
          setIsLoading(false);
        })
        .catch(err => {
          console.error('Failed to fetch tag details:', err);
          setError(err.message || 'Failed to load tag details');
          setIsLoading(false);
        });
    } else {
      setTagDetails([]);
    }
  }, [open, tagIds]);

  // Handle copy individual tag ID
  const handleCopyTagId = async (tagId: string) => {
    try {
      await navigator.clipboard.writeText(tagId);
      setCopySuccess(tagId);
      setTimeout(() => setCopySuccess(null), 2000);
    } catch (err) {
      console.error('Failed to copy tag ID:', err);
    }
  };

  // Handle copy all tag IDs
  const handleCopyAll = async () => {
    if (!tagIds || tagIds.length === 0) return;

    try {
      const allTagIds = tagIds.join('\n');
      await navigator.clipboard.writeText(allTagIds);
      setCopySuccess('all');
      setTimeout(() => setCopySuccess(null), 2000);
    } catch (err) {
      console.error('Failed to copy all tag IDs:', err);
    }
  };

  // Handle copy as JSON array
  const handleCopyAsJson = async () => {
    if (!tagIds || tagIds.length === 0) return;

    try {
      const jsonArray = JSON.stringify(tagIds, null, 2);
      await navigator.clipboard.writeText(jsonArray);
      setCopySuccess('json');
      setTimeout(() => setCopySuccess(null), 2000);
    } catch (err) {
      console.error('Failed to copy tag IDs as JSON:', err);
    }
  };

  // Get tag detail by ID
  const getTagDetail = (tagId: string): SpineTagDetails | undefined => {
    return tagDetails.find(t => t.id === tagId);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '400px' }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TagIcon color="primary" />
            <Typography variant="h6">SPINE Tags</Typography>
            {tagIds && tagIds.length > 0 && (
              <Chip
                label={`${tagIds.length} tag${tagIds.length !== 1 ? 's' : ''}`}
                size="small"
                color="success"
              />
            )}
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        {lessonName && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {lessonName}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent>
        {/* Success message */}
        {copySuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {copySuccess === 'all' && 'All tag IDs copied to clipboard!'}
            {copySuccess === 'json' && 'Tag IDs copied as JSON array!'}
            {copySuccess !== 'all' && copySuccess !== 'json' && `Tag ID "${copySuccess}" copied to clipboard!`}
          </Alert>
        )}

        {/* Loading state */}
        {isLoading && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} sx={{ mr: 2 }} />
            <Typography color="text.secondary">Loading tag details...</Typography>
          </Box>
        )}

        {/* Error state */}
        {error && !isLoading && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* No tags message */}
        {(!tagIds || tagIds.length === 0) && !isLoading && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <TagIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No SPINE Tags Generated
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Generate tags using the "Generate Tags" button to see SPINE tags here.
            </Typography>
          </Box>
        )}

        {/* Tags list */}
        {tagIds && tagIds.length > 0 && !isLoading && (
          <Box>
            {/* Copy options */}
            <Box sx={{ display: 'flex', gap: 1, mb: 2, mt: 2, flexWrap: 'wrap' }}>
              <Button
                size="small"
                startIcon={<ContentCopy />}
                onClick={handleCopyAll}
                variant="outlined"
              >
                Copy All IDs (Text)
              </Button>
              <Button
                size="small"
                startIcon={<ContentCopy />}
                onClick={handleCopyAsJson}
                variant="outlined"
              >
                Copy IDs as JSON
              </Button>
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Tag Details Accordions */}
            <Box sx={{ maxHeight: '500px', overflowY: 'auto' }}>
              {tagIds.map((tagId, index) => {
                const detail = getTagDetail(tagId);
                const hasMathExpression = detail?.regexTargetComposite;

                return (
                  <Accordion key={tagId} sx={{ mb: 1 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', pr: 2 }}>
                        <Chip label={`#${index + 1}`} size="small" color="primary" />
                        {hasMathExpression && (
                          <Tooltip title="Contains Math Expression" arrow>
                            <MathtasticIcon sx={{ fontSize: '1.2rem' }} />
                          </Tooltip>
                        )}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                            {detail?.competency?.title || 'Loading...'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }} noWrap>
                            {tagId}
                          </Typography>
                        </Box>
                        <Tooltip title="Copy tag ID">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopyTagId(tagId);
                            }}
                            sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}
                          >
                            <ContentCopy fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                      {detail ? (
                        <Box sx={{ pl: 1 }}>
                          {/* Competency Info */}
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                              Competency
                            </Typography>
                            <Typography variant="body2">
                              {detail.competency?.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {detail.competency?.uri}
                            </Typography>
                          </Box>

                          {/* Limiter Groups */}
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                              Limiter Groups ({detail.limiterGroups?.length || 0})
                            </Typography>
                            {detail.limiterGroups && detail.limiterGroups.length > 0 ? (
                              detail.limiterGroups.map(group => (
                                <Box
                                  key={group.id}
                                  sx={{
                                    p: 1.5,
                                    mb: 1,
                                    bgcolor: group.isMath ? 'rgba(255, 215, 0, 0.08)' : 'rgba(99, 102, 241, 0.08)',
                                    borderRadius: 1,
                                    border: '1px solid',
                                    borderColor: group.isMath ? 'rgba(255, 215, 0, 0.3)' : 'rgba(99, 102, 241, 0.3)',
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                    {group.isMath && (
                                      <MathtasticIcon sx={{ fontSize: '1rem' }} />
                                    )}
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                      {group.title}
                                    </Typography>
                                  </Box>
                                  {group.limiters && group.limiters.length > 0 && (
                                    <Box sx={{ pl: 2 }}>
                                      {group.limiters.map(limiter => (
                                        <Typography key={limiter.id} variant="body2" display="block" color="text.secondary">
                                          • {limiter.title}
                                        </Typography>
                                      ))}
                                    </Box>
                                  )}
                                </Box>
                              ))
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                No limiters selected
                              </Typography>
                            )}
                          </Box>

                          {/* Math Expression (regexTargetComposite) */}
                          {detail.regexTargetComposite && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Math Expression
                              </Typography>
                              <Box sx={{
                                p: 1.5,
                                bgcolor: 'rgba(255, 215, 0, 0.1)',
                                borderRadius: 1,
                                border: '1px solid rgba(255, 215, 0, 0.3)'
                              }}>
                                <Typography variant="body1" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                                  {detail.regexTargetComposite.title}
                                </Typography>
                                {detail.regexTargetComposite.regexTargets && (
                                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                    {detail.regexTargetComposite.regexTargets.length} elements
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          )}

                          {/* Created Date */}
                          {detail.created && (
                            <Typography variant="caption" color="text.secondary">
                              Created: {new Date(detail.created).toLocaleDateString()}
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Unable to load tag details
                        </Typography>
                      )}
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
